import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, ButtonBase, Paper, Stack, Typography, useTheme } from "@mui/material";
import { FC, ReactNode, useState } from "react";

type Props = {
	title?: string;
	description?: string;
	caption?: string;
	height?: number | string;
	prePlayImageUrl?: string;
	children?: ReactNode;
};

const PlayableContentFeatureTile: FC<Props> = ({ title, description, caption, height, prePlayImageUrl, children }) => {
	const theme = useTheme();
	const [revealed, setRevealed] = useState(false);

	return (
		<Paper
			elevation={2}
			sx={{
				flex: '1 1 auto',
				height: height || 240,
				overflow: 'hidden'
			}}
		>
			<Stack direction="row" className={revealed ? 'slider-panel revealed' : 'slider-panel'}>
				<Box
					sx={{
						width: '50%',
						height: height || 240,
						borderRadius: 1,
						backgroundColor: theme.palette.common.white,
						overflow: 'hidden'
					}}
					className={revealed ? 'playable-content revealed' : 'playable-content'}
				>
					<Box sx={{ p: 0.667 }}>
						{revealed && (
							children
						)}
					</Box>
				</Box>
				<Box
					className={revealed ? 'pre-play revealed' : 'pre-play'}
					sx={{
						position: 'relative',
						width: '50%',
						height: height || 240
					}}
				>
					<Box
						component="img"
						src={prePlayImageUrl}
						sx={{
							width: '100%',
							height: '100%',
							borderRadius: 1,
							objectFit: 'cover',
							objectPosition: '50% 50%',
							filter: 'brightness(0.5) blur(4px)'
						}}
					/>
					<Stack
						direction="row"
						spacing={2}
						sx={{
							position: 'absolute',
							top: 0,
							right: 0,
							bottom: 0,
							left: 0,
							alignItems: 'center',
							px: 4,
							py: 2
						}}
					>
						<ButtonBase
							sx={{
								position: 'relative',
								width: height ? +height / 1.6 : 120,
								height: height ? +height / 1.6 : 120,
								borderRadius: 1,
								'&:hover div': {
									opacity: 1
								}
							}}
							onClick={() => setRevealed(true)}
						>
							<Box
								component="img"
								src={prePlayImageUrl}
								sx={{
									width: height ? +height / 1.6 : 120,
									height: height ? +height / 1.6 : 120,
									borderRadius: 1,
									boxShadow: theme.shadows[16]
								}}
							/>
							<Box
								sx={{
									position: 'absolute',
									top: '50%',
									left: '50%',
									transform: 'translate(-50%, -50%)',
									opacity: 0.6,
									transition: 'opacity 0.2s ease'
								}}
							>
								<FontAwesomeIcon
									icon={['far', 'circle']}
									style={{
										fontSize: '4rem',
										color: theme.palette.common.white
									}}
								></FontAwesomeIcon>
							</Box>
							<Box
								sx={{
									position: 'absolute',
									top: '50%',
									left: '50%',
									transform: 'translate(calc(-50% + 2px), -50%)',
									opacity: 0.6,
									transition: 'opacity 0.2s ease'
								}}
							>
								<FontAwesomeIcon
									icon={['fas', 'play']}
									style={{
										fontSize: '1.8rem',
										color: theme.palette.common.white
									}}
								></FontAwesomeIcon>
							</Box>
						</ButtonBase>
						<Stack spacing={1} sx={{ height: height ? +height / 1.6 - 16 : 120, justifyContent: 'space-between' }}>
							<Stack spacing={1}>
								{title && (
									<Typography variant="h4" color="textPrimary" sx={{ fontSize: 'calc(1vw + 1vh + 0.5vmin)' }}>{title}</Typography>
								)}
								{description && (
									<Typography variant="body1" color="textSecondary">{description}</Typography>
								)}
							</Stack>
							{caption && (
								<Typography variant="caption" color="textSecondary" sx={{ display: { xs: 'none', sm: 'inline', md: 'inline', lg: 'inline', xl: 'inline' }}}>{caption}</Typography>
							)}
						</Stack>
					</Stack>
				</Box>
			</Stack>
		</Paper>
	);
};

export default PlayableContentFeatureTile;
